export type ImageArea = {
    height: number;
    width: number;
    x: number;
    y: number;
};

function createImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        // necessário para evitar erros de cross-origin
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });
}

function getRadianAngle(degreeValue: number): number {
    return (degreeValue * Math.PI) / 180;
}

/**
 * Adaptado de https://github.com/ricardo-ch/react-easy-crop/issues/176#issuecomment-749979498
 * @param imageSrc - URL da Imagem
 * @param pixelCrop - Objeto pixelCrop fornecido por react-easy-crop
 * @param rotation - Rotação
 * @param typeFile - Tipo do arquivo
 */
export async function getCroppedImg(
    imageSrc: string,
    croppedAreaPixels: ImageArea,
    rotation: number,
    fileType: string,
): Promise<string> {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        return '';
    }

    const maxSize = Math.max(image.width, image.height);
    const safeArea = Math.ceil(2 * ((maxSize / 2) * Math.sqrt(2)));

    // set each dimensions to double largest dimension to allow for a safe area for the
    // image to rotate in without being clipped by canvas context
    canvas.width = safeArea;
    canvas.height = safeArea;

    // translate canvas context to a central location on image to allow rotating around the center.
    ctx.translate(safeArea / 2, safeArea / 2);
    ctx.rotate(getRadianAngle(rotation));
    ctx.translate(-safeArea / 2, -safeArea / 2);

    // draw rotated image and store data.
    ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5);
    const data = ctx.getImageData(0, 0, safeArea, safeArea);

    const cropX = Math.round(croppedAreaPixels.x);
    const cropY = Math.round(croppedAreaPixels.y);
    const cropW = Math.round(croppedAreaPixels.width);
    const cropH = Math.round(croppedAreaPixels.height);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = cropW;
    canvas.height = cropH;

    // get fresh context after canvas resize
    const newCtx = canvas.getContext('2d');
    if (!newCtx) {
        return '';
    }

    // paste generated rotate image with correct offsets for x,y crop values.
    newCtx.putImageData(
        data,
        Math.round(-safeArea / 2 + image.width * 0.5 - cropX),
        Math.round(-safeArea / 2 + image.height * 0.5 - cropY),
    );

    // As Base64 string
    return canvas.toDataURL(fileType);
}
